import 'react-image-lightbox/style.css';
import { Swiper, SwiperSlide } from 'swiper/react';
import Lightbox from 'react-image-lightbox';
import { SwiperOptions } from 'swiper/types';
import { Thumbs } from 'swiper/modules';
import cn from 'classnames';
import Container from '~/components/common/container';
import { GalleryImage } from './components';
import { ImageWithPlaceholder } from '~/utils/image-plaiceholder';
import { experiencePhotoClickDataLayer } from '~/analytics/gtm/events';
import { useState } from 'react';
import ImageProxy from '~/components/common/image/image-proxy';

type ExperienceImageGalleryProps = {
   images: ImageWithPlaceholder[] | any[];
   video?: string;
   analytics: {
      package_name: string;
      package_price: string;
      event_location: string;
   };
   isItinerary?: boolean;
   className?: string;
};

interface BreakPoints {
   [ratio: string]: SwiperOptions;
   [width: number]: SwiperOptions;
}
const breakpoints: BreakPoints = {
   650: { slidesPerView: 6.5 },
   480: { slidesPerView: 5.5 },
   430: { slidesPerView: 4.3 },
};

export const ExperienceImageGallery = ({
   images = [],
   analytics,
   isItinerary,
   className,
}: ExperienceImageGalleryProps) => {
   const images_list = [...images];
   const picked_images = images_list.slice(1, 3);
   const items_count = images_list.length + 0;
   const [thumbsSwiper, setThumbsSwiper] = useState<any>(null);
   const [activeSwiper, setActiveSwiper] = useState(0);
   const [lightboxImage, setLightBoxImage] = useState({
      open: false,
      index: 0,
   });

   const getItemStyle = () => {
      if (picked_images.length === 1) return 'col-span-6 h-full row-span-2';
      if (picked_images.length === 2) return 'col-span-4 h-full !row-span-1';
      else return 'max-h-[132px] h-[132px]';
   };

   const getMainItemStyle = () => {
      if (!picked_images.length) return 'col-span-12';
      if (picked_images.length === 1) return 'col-span-6';
      if (picked_images.length === 3) return 'col-span-6 !row-span-1';
      else return 'col-span-8';
   };

   return (
      <div>
         <Container className={cn('ltr:!pr-0 rtl:!pl-0 hidden md:block', className)}>
            <div
               className={cn(
                  'grid grid-cols-12 gap-8 mt-4 mb-6',
                  picked_images.length === 2 && ' grid-rows-2'
               )}>
               <div
                  className={cn(
                     'row-span-2 rounded-2xl cursor-pointer min-h-[400px]',
                     getMainItemStyle()
                     // picked_images.length ? 'col-span-3' : 'col-span-4'
                  )}
                  onClick={() => {
                     setLightBoxImage({ index: 0, open: true });
                     experiencePhotoClickDataLayer(analytics);
                  }}>
                  <GalleryImage image={images[0]} priorty />
               </div>
               {picked_images.map((image, idx) => (
                  <div
                     key={idx}
                     className={cn(' rounded-xl relative cursor-pointer', getItemStyle())}
                     onClick={() => {
                        setLightBoxImage({ index: idx + 1, open: true });
                        experiencePhotoClickDataLayer(analytics);
                     }}>
                     <GalleryImage image={image} />
                     {idx === 1 && items_count > 3 && (
                        <div className="absolute inset-0 z-10 flex items-center justify-center text-xl font-bold text-white rounded-xl bg-black/60">
                           +{items_count - 3}
                        </div>
                     )}
                  </div>
               ))}
            </div>
         </Container>

         <div className="mb-4 md:hidden">
            <Swiper
               onRealIndexChange={({ activeIndex }) => setActiveSwiper(activeIndex)}
               modules={[Thumbs]}
               thumbs={{ swiper: thumbsSwiper }}
               spaceBetween={10}>
               {images.map((image, idx) => (
                  <SwiperSlide key={idx}>
                     <div
                        onClick={() => {
                           setLightBoxImage({ index: idx, open: true });
                           experiencePhotoClickDataLayer(analytics);
                        }}
                        className="h-[450px] w-full cursor-pointer">
                        <GalleryImage mobile priorty={idx === 0} image={image} />
                     </div>
                  </SwiperSlide>
               ))}
            </Swiper>

            <Swiper
               className={cn('!px-4 mt-2')}
               modules={[Thumbs]}
               watchSlidesProgress
               onSwiper={setThumbsSwiper}
               spaceBetween={10}
               slidesPerView={3.5}
               breakpoints={breakpoints}
               freeMode={true}>
               {images.map((image, idx) => (
                  <SwiperSlide key={`sd${idx}`}>
                     <div
                        className={cn(
                           'grid rounded-md p-0.5 border-2 borer-red-500',
                           activeSwiper === idx ? 'border-gray-300' : 'border-transparent'
                        )}>
                        <ImageProxy
                           width={77}
                           height={52}
                           src={image.img?.src || (image as string)}
                           className="object-cover rounded-md"
                        />
                     </div>
                  </SwiperSlide>
               ))}
            </Swiper>
         </div>

         {images?.length && lightboxImage.open ? (
            <div>
               <Lightbox
                  wrapperClassName="is-rtl"
                  onCloseRequest={() => setLightBoxImage(prev => ({ ...prev, open: false }))}
                  prevSrc={
                     !isItinerary
                        ? images[(lightboxImage.index - 1) % images.length]?.img.src
                        : (images[(images.length - 1) % images.length] as string)
                  }
                  nextSrc={
                     !isItinerary
                        ? images[(lightboxImage.index + 1) % images.length]?.img.src
                        : (images[(lightboxImage.index + 1) % images.length] as string)
                  }
                  mainSrc={
                     !isItinerary
                        ? images[lightboxImage.index]?.img.src
                        : (images[lightboxImage.index] as string)
                  }
                  onMoveNextRequest={() =>
                     setLightBoxImage(prev => ({
                        ...prev,
                        index: (prev.index + 1) % images.length,
                     }))
                  }
                  onMovePrevRequest={() =>
                     setLightBoxImage(prev => ({
                        ...prev,
                        index: (prev.index - 1) % images.length,
                     }))
                  }
               />
            </div>
         ) : null}
      </div>
   );
};
